import CheckIcon from '@mui/icons-material/Check';
import { styled, useTheme } from '@mui/material/styles';
import { Button, SingleLineText, SmallInfoCard, Tag, TitlePart } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import { MonthlyPrimeHeaderAmounts } from '../../types/monthlyPrime';
import { formatPrimeEuro, formatPrimeEuroWithSign, splitPrimeMonthLabel } from '../../utils/monthlyPrime.utils';

export type MonthlyPrimeHeaderProps = {
  tamDisplayName: string;
  monthLabel: string;
  amounts?: MonthlyPrimeHeaderAmounts;
  loading: boolean;
};

type AmountCardConfig = {
  label: string;
  value: string;
  highlighted?: boolean;
  negative?: boolean;
  positive?: boolean;
};

const MonthlyPrimeHeader = ({ tamDisplayName, monthLabel, amounts, loading }: MonthlyPrimeHeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['primes']);
  const { monthName, year } = splitPrimeMonthLabel(monthLabel);

  const formatAmount = (value: number | undefined) => (loading ? '...' : formatPrimeEuro(value ?? 0));

  const formatRegularisation = (value: number | undefined) => (loading ? '...' : formatPrimeEuroWithSign(value ?? 0));

  const amountCards: AmountCardConfig[] = [
    { label: t('headerCommission'), value: formatAmount(amounts?.commission) },
    { label: t('headerBonus'), value: formatAmount(amounts?.bonus) },
    {
      label: t('headerRegul'),
      value: formatRegularisation(amounts?.regularisation),
      negative: !loading && (amounts?.regularisation ?? 0) < 0,
      positive: !loading && (amounts?.regularisation ?? 0) > 0,
    },
    {
      label: t('headerTotalToReceive'),
      value: formatAmount(amounts?.total),
      highlighted: true,
    },
  ];

  return (
    <HeaderContainer>
      <LeftBlock>
        <SectionLabel text={t('headerMonthDetail')} variant="caption" />
        <MonthTitleRow>
          <TitlePart
            textTitle={monthName || '—'}
            span={year}
            variant="heading6"
            sx={(theme) => ({
              fontWeight: 600,
              color: theme.palette.neutral.black,
              textAlign: 'center',
              '& span': {
                marginLeft: theme.spacing(1),
                fontWeight: 400,
                color: theme.palette.neutral[50],
              },
            })}
          />
        </MonthTitleRow>
        <TamName text={tamDisplayName || '—'} variant="body1Regular" />
        <Tag
          text={t('monthStatusPending')}
          backgroundColor={theme.palette.beige[40]}
          color={theme.palette.beige[100]}
        />
      </LeftBlock>

      <VerticalDivider />

      <AmountCardsRow>
        {amountCards.map((card) => (
          <AmountCardSlot
            key={card.label}
            highlighted={card.highlighted}
            negative={card.negative}
            positive={card.positive}
          >
            <SmallInfoCard title={card.label} value={card.value} loading={loading} />
          </AmountCardSlot>
        ))}
      </AmountCardsRow>

      <ActionsBlock>
        <Button disable textButton={t('validateMonth')} emoji={<CheckIcon />} buttonType="button" size="large" />
      </ActionsBlock>
    </HeaderContainer>
  );
};

export default MonthlyPrimeHeader;

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '200px 1px 1fr auto',
  alignItems: 'center',
  columnGap: theme.spacing(3),
  rowGap: theme.spacing(2),
  marginTop: theme.spacing(3),
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.neutral.white,
  borderRadius: '16px',
  border: `1px solid ${theme.palette.neutral[20]}`,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

const LeftBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: theme.spacing(1),
  width: '100%',
  minHeight: '100%',
  [theme.breakpoints.down('md')]: {
    minHeight: 0,
  },
}));

const SectionLabel = styled(SingleLineText)(({ theme }) => ({
  color: theme.palette.neutral[50],
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontWeight: 600,
}));

const MonthTitleRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
}));

const TamName = styled(SingleLineText)(({ theme }) => ({
  color: theme.palette.neutral[60],
}));

const VerticalDivider = styled('div')(({ theme }) => ({
  width: '1px',
  alignSelf: 'stretch',
  backgroundColor: theme.palette.neutral[20],
  minHeight: '100%',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const AmountCardsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  minWidth: 0,
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center',
  },
}));

const AmountCardSlot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'negative' && prop !== 'positive',
})<{ highlighted?: boolean; negative?: boolean; positive?: boolean }>(({ theme, highlighted, negative, positive }) => ({
  flex: '1 1 120px',
  minWidth: '120px',
  maxWidth: '100%',
  [theme.breakpoints.down('sm')]: {
    minWidth: 'calc(50% - 8px)',
    flex: '1 1 calc(50% - 8px)',
  },
  '& > div': {
    border: `1px solid ${highlighted ? theme.palette.red[40] : theme.palette.neutral[20]}`,
    backgroundColor: highlighted ? theme.palette.red[20] : theme.palette.neutral.white,
  },
  '& h2': {
    color: highlighted ? theme.palette.red[60] : theme.palette.neutral[50],
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontSize: '12px',
    fontWeight: 600,
  },
  '& h2 + span': {
    color: negative
      ? theme.palette.red[100]
      : positive
      ? theme.palette.green[100]
      : highlighted
      ? theme.palette.red[60]
      : theme.palette.neutral.black,
    fontSize: theme.typography.h6.fontSize,
    lineHeight: theme.typography.h6.lineHeight,
  },
}));

const ActionsBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));
